import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Card, CardHeader, CardTitle, CardContent } from "./common/Card";
import Button from "./common/Button";
import { ArrowLeft, Mail, Building2, CheckCircle, Clock } from "lucide-react";
import config from "../config";
import Spinner from "./common/Spinner";
import { formatDateTimeDMY } from "../lib/ScheduleUtils";
import { getOrganizationDisplayName } from "../utils/utils";
import { Badge } from "./common/Badge";
import useBrandTranslation from "../hooks/useBrandTranslation";
import { apiJsonHeaders } from "../utils/apiLocale";

export default function DoctorAssociationMessages() {
  const { t } = useBrandTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState("");
  const [markingAsRead, setMarkingAsRead] = useState(new Set());

  useEffect(() => {
    if (!user || user.role !== "doctor") {
      navigate("/");
      return;
    }
    loadMessages();
  }, [user, navigate]);

  const loadMessages = async () => {
    try {
      setLoadingMessages(true);
      setError("");
      const response = await fetch(
        `${config.baseUrlApi}/professionals/${user.id}/messages`,
        { headers: apiJsonHeaders() }
      );
      if (!response.ok) throw new Error(t("app.messaging.loadMessagesError", "Error al cargar mensajes"));
      const result = await response.json();
      setMessages(result.data || []);
    } catch (err) {
      console.error("Error loading messages:", err);
      setError(t("app.messaging.loadMessagesErrorDetail", "Error al cargar los mensajes recibidos"));
    } finally {
      setLoadingMessages(false);
    }
  };

  const markAsRead = async (recipientId) => {
    if (markingAsRead.has(recipientId)) return;

    try {
      setMarkingAsRead((prev) => new Set(prev).add(recipientId));
      const response = await fetch(
        `${config.baseUrlApi}/message-recipients/${recipientId}/read`,
        {
          method: "PATCH",
          headers: apiJsonHeaders(),
        }
      );

      if (!response.ok) throw new Error(t("app.messaging.markReadError", "Error al marcar como leído"));

      // Actualizar el estado local
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === recipientId
            ? { ...msg, read_at: new Date().toISOString() }
            : msg
        )
      );
    } catch (err) {
      console.error("Error marking as read:", err);
    } finally {
      setMarkingAsRead((prev) => {
        const newSet = new Set(prev);
        newSet.delete(recipientId);
        return newSet;
      });
    }
  };

  const getAssociationName = (association) => {
    return getOrganizationDisplayName(association);
  };

  if (!user || user.role !== "doctor") {
    return null;
  }

  const unreadCount = messages.filter((msg) => !msg.read_at).length;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Mensajes de Asociaciones
              </h1>
              <p className="text-gray-600">
                Mensajes recibidos de las asociaciones a las que estás suscrito
              </p>
            </div>
            {unreadCount > 0 && (
              <Badge variant="default" className="text-sm">
                {unreadCount} {unreadCount === 1 ? "mensaje sin leer" : "mensajes sin leer"}
              </Badge>
            )}
          </div>
        </div>

        {/* Mensajes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Mensajes Recibidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
                {error}
              </div>
            )}

            {loadingMessages ? (
              <div className="flex justify-center py-8">
                <Spinner className="w-8 h-8" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">
                  No has recibido ningún mensaje aún.
                </p>
                <p className="text-gray-400 text-sm mt-2">
                  Los mensajes de las asociaciones a las que estés suscrito aparecerán aquí.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((recipient) => {
                  const message = recipient.message;
                  if (!message) return null;

                  const association = message.association;
                  const isRead = !!recipient.read_at;
                  const isMarking = markingAsRead.has(recipient.id);

                  return (
                    <div
                      key={recipient.id}
                      className={`border rounded-lg p-4 transition-colors ${
                        isRead
                          ? "bg-white hover:bg-gray-50"
                          : "bg-blue-50 border-blue-200 hover:bg-blue-100"
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-3 flex-1">
                          {association?.picture ? (
                            <img
                              src={association.picture}
                              alt={getAssociationName(association)}
                              className="w-12 h-12 object-cover rounded-lg"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-purple-200 rounded-lg flex items-center justify-center">
                              <Building2 className="w-6 h-6 text-purple-600" />
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-lg">
                                {getAssociationName(association)}
                              </h3>
                              {!isRead && (
                                <Badge variant="default" className="text-xs">
                                  Nuevo
                                </Badge>
                              )}
                            </div>
                            <h4 className="font-medium text-gray-900 mb-1">
                              {message.subject}
                            </h4>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Clock className="w-4 h-4" />
                              <span>
                                {formatDateTimeDMY(message.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        {!isRead && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => markAsRead(recipient.id)}
                            disabled={isMarking}
                            className="ml-4"
                          >
                            {isMarking ? (
                              <>
                                <Spinner className="w-3 h-3 mr-2" />
                                Marcando...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Marcar como leído
                              </>
                            )}
                          </Button>
                        )}
                      </div>

                      <div
                        className="text-gray-700 mt-3 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: message.message }}
                      />

                      {message.images && message.images.length > 0 && (
                        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2">
                          {message.images.map((img, idx) => (
                            <img
                              key={idx}
                              src={img}
                              alt={`Imagen ${idx + 1}`}
                              className="w-full h-32 object-cover rounded border cursor-pointer hover:opacity-90"
                              onClick={() => window.open(img, "_blank")}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

